import { useEffect, useRef } from 'react';
import { StatusIndicator } from './StatusIndicator';
import { useProjectRisk } from '@/hooks/useProjectRisk';

interface ProjectRiskBadgeProps {
    projectId: number;
}

const formatDate = (date: Date): string =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const ProjectRiskBadge = ({ projectId }: ProjectRiskBadgeProps) => {
    const riskInfo = useProjectRisk(projectId);
    const riskStatus = riskInfo?.riskStatus ?? null;
    const liveRegionRef = useRef<HTMLDivElement>(null);
    const prevStatusRef = useRef<string | null>(null);

    // Announce risk status changes to screen readers
    useEffect(() => {
        if (!riskStatus || !liveRegionRef.current) return;
        if (prevStatusRef.current !== null && prevStatusRef.current !== riskStatus) {
            if (riskStatus === 'at_risk') {
                liveRegionRef.current.textContent =
                    'Warning: This project is now at risk of missing its deadline';
            } else if (riskStatus === 'overdue') {
                liveRegionRef.current.textContent =
                    'Warning: This project is overdue and has missed its deadline';
            } else if (riskStatus === 'on_track') {
                liveRegionRef.current.textContent = 'Project is back on track';
            } else if (riskStatus === 'unknown') {
                liveRegionRef.current.textContent = 'Project status is now unknown';
            }
        }
        prevStatusRef.current = riskStatus;
    }, [riskStatus]);

    if (!riskInfo) return null;

    return (
        <div data-testid="project-risk-badge">
            {/* Screen reader live region for status change announcements */}
            <div
                ref={liveRegionRef}
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
            />

            <StatusIndicator status={riskInfo.riskStatus} />

            {/* Inline date comparison per AC5: "Committed: Feb 28 | Estimated: Mar 5 | 5 days late" */}
            {riskInfo.committedDate && (
                <div
                    className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700"
                    aria-label="Date comparison"
                >
                    <span aria-label={`Committed date: ${formatDate(riskInfo.committedDate)}`}>
                        <span className="font-medium text-gray-600">Committed:</span>{' '}
                        <span className="font-mono">{formatDate(riskInfo.committedDate)}</span>
                    </span>

                    {riskInfo.estimatedCompletion && (
                        <>
                            <span className="text-gray-400" aria-hidden="true">|</span>
                            <span aria-label={`Estimated date: ${formatDate(riskInfo.estimatedCompletion)}`}>
                                <span className="font-medium text-gray-600">Estimated:</span>{' '}
                                <span
                                    className={`font-mono ${riskInfo.isAtRisk ? 'text-red-600 font-semibold' : 'text-emerald-600'}`}
                                >
                                    {formatDate(riskInfo.estimatedCompletion)}
                                </span>
                            </span>

                            {riskInfo.isAtRisk && riskInfo.daysDifference > 0 && (
                                <>
                                    <span className="text-gray-400" aria-hidden="true">|</span>
                                    <span
                                        className="font-semibold text-red-600"
                                        data-testid="days-late"
                                        aria-label={`${riskInfo.daysDifference} working ${riskInfo.daysDifference === 1 ? 'day' : 'days'} late`}
                                    >
                                        {riskInfo.daysDifference} working{' '}
                                        {riskInfo.daysDifference === 1 ? 'day' : 'days'} late
                                    </span>
                                </>
                            )}

                            {!riskInfo.isAtRisk &&
                                riskInfo.riskStatus === 'on_track' &&
                                riskInfo.daysDifference < 0 && (
                                    <>
                                        <span className="text-gray-400" aria-hidden="true">|</span>
                                        <span
                                            className="font-semibold text-emerald-600"
                                            data-testid="days-early"
                                            aria-label={`${Math.abs(riskInfo.daysDifference)} working ${Math.abs(riskInfo.daysDifference) === 1 ? 'day' : 'days'} early`}
                                        >
                                            {Math.abs(riskInfo.daysDifference)} working{' '}
                                            {Math.abs(riskInfo.daysDifference) === 1 ? 'day' : 'days'} early
                                        </span>
                                    </>
                                )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
